import { useState, useEffect, useRef, useMemo } from "react";
import { getUnitholderDetails } from "../services/auth.service";
import { useTheme } from "next-themes";
import { useAuth } from "../store/AuthContext";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useWindowSize from "../hooks/useWindowSize";
import { tabButton } from "../components/customStyles";
import InvestorPortfolio from "../pages/portfolio";
import InvestorStatement from "../pages/statement";
import Requests from "../pages/requests";
import Goals from "../pages/goals";

const PortfolioLayout = () => {
  const { t } = useTranslation();

  const [activeSubMenu, setActiveSubMenu] = useState("portfolio");
  const [unitholder, setUnitholder] = useState(null);
  const [portfolioFunds, setPortfolioFunds] = useState(null);
  const [loadingUnitholder, setLoadingUnitholder] = useState(true);

  const screenSize = useWindowSize();

  const { theme } = useTheme();

  const { user } = useAuth();
  const { funds, loadingFunds } = useOutletContext();

  const mountedRef = useRef(true);
  const ranOnceRef = useRef(false);

  const fundMap = useMemo(() => {
    const arr = funds ?? [];
    return new Map(arr.map((f) => [f.symbol_code, f]));
  }, [funds]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // guard: don't run while funds are loading, or if user has no folio, or if we've already run
    if (loadingFunds) return;
    if (!funds) return;
    if (!user?.data?.folio_no) return;
    if (ranOnceRef.current) return; // remove this line if you want to re-run on every investFunds change

    ranOnceRef.current = true;
    (async () => {
      setLoadingUnitholder(true);
      try {
        const unitholderResponse = await getUnitholderDetails(user.data.email, user.data.folio_no);

        if (!unitholderResponse.error && Array.isArray(unitholderResponse.data) && unitholderResponse.data.length > 0) {
          const localDataBeforeRender = unitholderResponse.data[0];
          const unitHolderFunds = JSON.parse(localDataBeforeRender.balance_unit || "[]");

          if (unitHolderFunds.length !== 0) {
            const updatedUnitholderFunds = unitHolderFunds.map((item) => {
              const copy = fundMap.get(item.fund_code) || {}; // robust fallback
              return {
                ...copy,
                ...item,
                navData: copy?.navData ?? null,
                fund_name: copy?.fund_name ?? "",
              };
            });

            if (mountedRef.current) setPortfolioFunds(updatedUnitholderFunds);
          }

          if (mountedRef.current) setUnitholder(localDataBeforeRender);
        }
      } catch (err) {
        // console.error("Error in setDataOnMount:", err);
      } finally {
        if (mountedRef.current) setLoadingUnitholder(false);
      }
    })();
  }, [loadingFunds, funds, user?.data?.folio_no, fundMap]); // fundMap included to re-evaluate if investFunds.data changes

  return (
    <div className='d-flex flex-column align-items-center flex-nowrap w-100'>
      {/* PORTFOLIO MENU TABS */}
      <div
        className='d-inline-flex align-items-center flex-nowrap gap-2 p-2'
        style={{
          borderRadius: "999px",
          maxWidth: "100%",
          overflowX: "auto",
          backgroundColor: "var(--theme-tabs-bg)",
          scrollbarWidth: "none",
        }}
      >
        <button
          className={`btn flex-shrink-0${activeSubMenu === "portfolio" ? " active" : ""}`}
          type='button'
          onClick={() => setActiveSubMenu("portfolio")}
          style={{
            lineHeight: "1",
            fontSize: "0.8rem",
            padding: "10px 16px 11px",
            minWidth: "72px",
            borderRadius: "99px",
            ...tabButton(theme),
          }}
        >
          {t("portfolio.default")}
        </button>
        <button
          className={`btn flex-shrink-0${activeSubMenu === "statement" ? " active" : ""}`}
          type='button'
          onClick={() => setActiveSubMenu("statement")}
          style={{
            lineHeight: "1",
            fontSize: "0.8rem",
            padding: "10px 16px 11px",
            minWidth: "72px",
            borderRadius: "99px",
            ...tabButton(theme),
          }}
        >
          {/* <svg
            xmlns='http://www.w3.org/2000/svg'
            width='20'
            height='20'
            fill='none'
            stroke='currentColor'
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth='1.25'
            className='lucide lucide-scroll-text-icon lucide-scroll-text'
            viewBox='0 0 24 24'
          >
            <path d='M15 12h-5M15 8h-5M19 17V5a2 2 0 0 0-2-2H4'></path>
            <path d='M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3'></path>
          </svg> */}
          {t("portfolio.tabs.stmnt")}
        </button>
        <button
          className={`btn flex-shrink-0${activeSubMenu === "requests" ? " active" : ""}`}
          type='button'
          onClick={() => setActiveSubMenu("requests")}
          style={{
            lineHeight: "1",
            fontSize: "0.8rem",
            padding: "10px 16px 11px",
            minWidth: "72px",
            borderRadius: "99px",
            ...tabButton(theme),
          }}
        >
          {t("portfolio.tabs.req")}
        </button>
        <button
          className={`btn flex-shrink-0${activeSubMenu === "goals" ? " active" : ""}`}
          type='button'
          onClick={() => setActiveSubMenu("goals")}
          style={{
            lineHeight: "1",
            fontSize: "0.8rem",
            padding: "10px 16px 11px",
            minWidth: "72px",
            borderRadius: "99px",
            ...tabButton(theme),
          }}
        >
          {t("app.cards.goals")}
        </button>
      </div>

      {/* PORTFOLIO CONTENT */}
      <div
        className='w-100'
        style={{
          borderRadius: "20px",
          marginTop: "12px",
          backgroundColor: "var(--theme-bg)",
          height: screenSize <= 575 ? "calc(100dvh - 212px)" : screenSize <= 991 && screenSize >= 576 ? "calc(100dvh - 265px)" : "calc(100dvh - 192px)",
        }}
      >
        <div
          className={
            "row w-100 mx-auto flex-column position-relative flex-nowrap" + (theme === "dark" && activeSubMenu === "statement" ? " add-custom-scroll-dark" : " add-custom-scroll")
          }
          style={{
            rowGap: "1rem",
            height:
              activeSubMenu === "statement"
                ? screenSize >= 992
                  ? "calc(100dvh - 192px)"
                  : screenSize <= 575
                    ? "calc(100dvh - 212px)"
                    : "calc(100dvh - 265px)"
                : screenSize >= 992
                  ? "calc(100dvh - 192px)"
                  : screenSize <= 575
                    ? "calc(100dvh - 212px)"
                    : "calc(100dvh - 265px)",
            padding: screenSize >= 576 ? ((activeSubMenu === 'requests' || activeSubMenu === 'goals' || activeSubMenu === 'portfolio') ? '0' : '.8rem 1rem') : (activeSubMenu === 'statement' ? '.25rem .75rem' : "0"),
            overflowY: "hidden",
            scrollbarWidth: screenSize >= 768 && activeSubMenu === "statement" ? "" : "none",
            borderBottomLeftRadius: screenSize <= 575 ? "20px" : "",
            borderBottomRightRadius: screenSize <= 575 ? "20px" : "",
          }}
        >
          {activeSubMenu === "portfolio" ? (
            <InvestorPortfolio unitholder={unitholder} loadingUnitholder={loadingUnitholder} funds={funds} portfolioFunds={portfolioFunds} />
          ) : activeSubMenu === "statement" ? (
            <InvestorStatement funds={funds} portfolioFunds={portfolioFunds} setActiveSubMenu={setActiveSubMenu} />
          ) : activeSubMenu === "requests" ? (
            <Requests funds={funds} loadingFunds={loadingFunds} portfolioFunds={portfolioFunds} setActiveSubMenu={setActiveSubMenu} />
          ) : activeSubMenu === "goals" ? (
            <Goals unitholder={unitholder} portfolioFunds={portfolioFunds} setActiveSubMenu={setActiveSubMenu} />
          ) : (
            ""
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioLayout;
